import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Menu, X, LogOut, ChevronDown,
} from "lucide-react";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { usePet } from "@/hooks/usePet";
import { Button } from "@/components/ui/button";
import { PetAvatar } from "@/components/PetAvatar";
import { SLUG_MAP, type SupportedLocale } from "@/lib/seo";

/** Height constant for layout calculations */
export const GLOBAL_NAV_HEIGHT = 56;

/* ── Helper: get localized path for a SLUG_MAP page ── */
function localizedMarketingPath(pageId: string, locale: SupportedLocale): string {
  const entry = SLUG_MAP[pageId]?.[locale];
  if (!entry || !entry.slug) return "/";
  const prefix = locale === "en" ? "" : `/${locale}`;
  return `${prefix}/${entry.slug}`;
}

/* ── Helper: get localized app path ── */
function localizedAppPath(path: string, locale: SupportedLocale): string {
  if (locale === "en") return path;
  return `/${locale}${path}`;
}

/* ── Detect locale from URL ── */
function getUrlLocale(pathname: string): SupportedLocale {
  if (pathname.startsWith("/ru")) return "ru";
  if (pathname.startsWith("/es")) return "es";
  return "en";
}

export const GlobalNavbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { pet } = usePet();

  const locale = getUrlLocale(location.pathname);

  const tl = (key: string, fallback?: string): string =>
    t(key, { lng: locale, defaultValue: fallback ?? key });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const cleanPath = location.pathname.replace(/^\/(ru|es)/, "").replace(/^$/, "/");
  const isAuth = cleanPath === "/auth";

  const handleNav = useCallback((path: string) => {
    setMobileOpen(false);
    setUserMenuOpen(false);
    navigate(path);
  }, [navigate]);

  const handleSignOut = useCallback(async () => {
    setUserMenuOpen(false);
    setMobileOpen(false);
    await signOut();
    navigate(locale === "en" ? "/" : `/${locale}`);
  }, [signOut, navigate, locale]);

  // Close user dropdown on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [userMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // ── Marketing nav links (unauthenticated) ──
  const marketingLinks = [
    { label: tl("nav.ai_companion", "AI Companion"), path: localizedMarketingPath("ai-pet-companion", locale) },
    { label: tl("nav.memorial", "Memorial"), path: localizedMarketingPath("pet-memorial", locale) },
    { label: tl("nav.pricing", "Pricing"), path: localizedMarketingPath("pricing", locale) },
    { label: tl("nav.about", "About"), path: localizedMarketingPath("about", locale) },
    { label: "Blog", path: `${locale === "en" ? "" : `/${locale}`}/blog` },
  ];

  // ── App nav links (authenticated) ──
  const appLinks = [
    { label: tl("nav.chat_short", "Chat"), path: localizedAppPath("/app/chat", locale) },
    { label: tl("nav.journal_short", "Journal"), path: localizedAppPath("/app/journal", locale) },
    { label: tl("nav.garden", "Garden"), path: localizedAppPath("/garden", locale) },
  ];

  const navLinks = user ? appLinks : marketingLinks;

  const isActivePath = (path: string) => {
    const current = location.pathname;
    if (path === current) return true;
    // Also match without trailing slash
    if (path.endsWith("/") && current === path.slice(0, -1)) return true;
    if (!path.endsWith("/") && current === path + "/") return true;
    return false;
  };

  const logoTarget = locale === "en" ? "/" : `/${locale}`;

  // ── User dropdown menu items ──
  const userDropdownItems = [
    { emoji: "⚙️", label: tl("nav.settings_short", "Settings"), action: () => handleNav(localizedAppPath("/app/settings", locale)) },
    { emoji: "💎", label: tl("nav.pricing", "Pricing"), action: () => handleNav(localizedMarketingPath("pricing", locale)) },
    { emoji: "ℹ️", label: tl("nav.about", "About"), action: () => handleNav(localizedMarketingPath("about", locale)) },
  ];

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-[100] border-b bg-background/90 backdrop-blur-2xl border-border/15 transition-colors duration-300"
        style={{ height: GLOBAL_NAV_HEIGHT }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between gap-3">

          {/* ──── Left: Logo ──── */}
          <motion.button
            className="flex items-center gap-1.5 cursor-pointer shrink-0"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => handleNav(logoTarget)}
          >
            <span className="font-extrabold text-sm text-foreground tracking-[2px]">PETERNITY</span>
            <span className="text-xs">🐾</span>
          </motion.button>

          {/* ──── Center: Nav links (desktop) ──── */}
          <div className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => handleNav(link.path)}
                className={`px-3 py-1.5 rounded-xl text-[13px] font-semibold transition-all whitespace-nowrap ${
                  isActivePath(link.path)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]"
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* ──── Right side ──── */}
          <div className="flex items-center gap-2 shrink-0">

            {/* Language switcher (desktop) */}
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>

            {/* ── Unauthenticated: Sign In + Get Started ── */}
            {!user && !isAuth && (
              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleNav(localizedAppPath("/auth", locale))}
                  className="rounded-xl font-semibold text-xs h-8 px-4"
                >
                  {tl("nav.sign_in", "Sign In")}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleNav(localizedAppPath("/auth", locale))}
                  className="rounded-xl font-bold text-xs h-8 px-4"
                >
                  {tl("nav.get_started_free", "Start Free")}
                </Button>
              </div>
            )}

            {/* ── Authenticated: User avatar + dropdown ── */}
            {user && (
              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-xl hover:bg-foreground/[0.04] transition-colors"
                >
                  {pet ? (
                    <PetAvatar avatarUrl={pet.avatar_url} mode={pet.mode} size="sm" isMemorial={pet.mode === "memorial"} className="ring-0" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                      {user.email?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}
                  <span className="text-xs font-semibold text-foreground/70 max-w-[80px] truncate">
                    {pet?.name || tl("nav.account", "Account")}
                  </span>
                  <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -4 }}
                      transition={{ duration: 0.12 }}
                      className="absolute right-0 top-full mt-1.5 w-52 rounded-2xl p-1.5 shadow-lg bg-card/95 backdrop-blur-xl border border-border/20 z-50"
                    >
                      {userDropdownItems.map((item, i) => (
                        <button
                          key={i}
                          onClick={item.action}
                          className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm font-medium text-foreground/80 hover:bg-foreground/[0.04] transition-colors"
                        >
                          <span className="text-sm">{item.emoji}</span>
                          {item.label}
                        </button>
                      ))}
                      <div className="my-1 border-t border-border/15" />
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm font-medium text-destructive/80 hover:bg-destructive/5 transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        {tl("nav.sign_out", "Sign Out")}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* ── Mobile hamburger ── */}
            <button
              className="md:hidden p-1.5 rounded-lg hover:bg-foreground/5 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? tl("nav.close_menu") : tl("nav.open_menu")}
            >
              <AnimatePresence mode="wait" initial={false}>
                {mobileOpen ? (
                  <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                    <X className="h-5 w-5 text-foreground" />
                  </motion.div>
                ) : (
                  <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                    <Menu className="h-5 w-5 text-foreground" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </nav>

      {/* ── Mobile full-screen overlay ── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed inset-0 z-[99] bg-background/98 backdrop-blur-xl"
            style={{ top: GLOBAL_NAV_HEIGHT }}
          >
            <div className="flex flex-col h-full px-6 py-6 overflow-y-auto">
              {/* Nav links */}
              <div className="space-y-1">
                {navLinks.map((link) => (
                  <button
                    key={link.path}
                    onClick={() => handleNav(link.path)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                      isActivePath(link.path) ? "bg-primary/10 text-primary" : "text-foreground hover:bg-foreground/5"
                    }`}
                  >
                    {link.label}
                  </button>
                ))}
              </div>

              {/* Authenticated extra links */}
              {user && (
                <>
                  <div className="my-3 border-t border-border/15" />
                  <div className="space-y-1">
                    {userDropdownItems.map((item, i) => (
                      <button
                        key={i}
                        onClick={item.action}
                        className="w-full text-left px-4 py-3 rounded-xl text-base font-medium text-foreground hover:bg-foreground/5 transition-colors"
                      >
                        {item.emoji} {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Language switcher */}
              <div className="my-3 border-t border-border/15" />
              <div className="flex justify-center py-2">
                <LanguageSwitcher />
              </div>

              {/* Auth buttons (unauthenticated) */}
              {!user && !isAuth && (
                <>
                  <div className="my-3 border-t border-border/15" />
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      onClick={() => handleNav(localizedAppPath("/auth", locale))}
                      className="w-full rounded-xl font-semibold"
                    >
                      {tl("nav.sign_in", "Sign In")}
                    </Button>
                    <Button
                      onClick={() => handleNav(localizedAppPath("/auth", locale))}
                      className="w-full rounded-xl font-bold"
                    >
                      {tl("nav.get_started_free", "Start Free")}
                    </Button>
                  </div>
                </>
              )}

              {/* Sign out (authenticated) */}
              {user && (
                <>
                  <div className="my-3 border-t border-border/15" />
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-3 rounded-xl text-base font-medium text-destructive/80 hover:bg-destructive/5 transition-colors"
                  >
                    🚪 {tl("nav.sign_out", "Sign Out")}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
